import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  titleCandidates,
  titleField,
  type EditorField,
  type EditorList,
  type ResolvedList,
} from '@/pages/apps/designs/components/design-state';
import FieldRow from '@/pages/apps/designs/components/field-row';
import type { FieldType } from '@/types/platform';
import { Plus, Trash2 } from 'lucide-react';

interface ListEditorProps {
  list: ResolvedList;
  fieldTypes: FieldType[];
  onChange: (patch: Partial<Omit<EditorList, 'fields'>>) => void;
  onRemove: () => void;
  onAddField: () => void;
  onFieldChange: (fieldId: string, patch: Partial<EditorField>) => void;
  onFieldRemove: (fieldId: string) => void;
}

const MAX_NAME_LENGTH = 60;

// One list: what it is called, which field names a record, and its fields.
const ListEditor = ({
  list,
  fieldTypes,
  onChange,
  onRemove,
  onAddField,
  onFieldChange,
  onFieldRemove,
}: ListEditorProps) => {
  const nameId = `${list.id}-name`;
  const singularId = `${list.id}-singular`;
  const titleId = `${list.id}-title`;
  const candidates = titleCandidates(list);
  const title = titleField(list);

  return (
    <Card>
      <CardHeader className="gap-4">
        <div className="grid gap-3 sm:grid-cols-[minmax(0,1fr)_minmax(0,1fr)_auto] sm:items-end">
          <div className="space-y-1.5">
            <Label htmlFor={nameId}>List name</Label>
            <Input
              id={nameId}
              value={list.name}
              onChange={(event) => onChange({ name: event.target.value })}
              placeholder="Jobs"
              maxLength={MAX_NAME_LENGTH}
              required
            />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor={singularId}>One of them is called</Label>
            <Input
              id={singularId}
              value={list.singular}
              onChange={(event) => onChange({ singular: event.target.value })}
              placeholder="Job"
              maxLength={MAX_NAME_LENGTH}
            />
          </div>

          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="justify-self-start"
            onClick={onRemove}
          >
            <Trash2 aria-hidden="true" />
            Remove list<span className="sr-only"> {list.name}</span>
          </Button>
        </div>

        <p className="text-muted-foreground text-xs">
          Key: <code>{list.key}</code>
        </p>
      </CardHeader>

      <CardContent className="space-y-4">
        <div className="space-y-1.5">
          <Label htmlFor={titleId}>Title field</Label>
          {candidates.length > 0 && title ? (
            <Select value={title.id} onValueChange={(value) => onChange({ titleFieldId: value })}>
              <SelectTrigger id={titleId} className="w-full sm:w-64">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {candidates.map((field) => (
                  <SelectItem key={field.id} value={field.id}>
                    {field.label.trim() || 'Unnamed field'}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          ) : (
            <Select disabled>
              <SelectTrigger id={titleId} className="w-full sm:w-64">
                <SelectValue placeholder="No field can be the title yet" />
              </SelectTrigger>
              <SelectContent />
            </Select>
          )}
          <p className="text-muted-foreground text-xs">
            {candidates.length > 0
              ? 'The field that names each record in this list.'
              : 'Add a short text, email, or phone field to name each record. Until then the first field is used.'}
          </p>
        </div>

        <div className="space-y-2">
          <h3 className="text-sm font-medium">Fields</h3>
          {list.fields.length === 0 ? (
            <p className="text-muted-foreground text-sm">A list needs at least one field.</p>
          ) : (
            <ul className="space-y-2">
              {list.fields.map((field) => (
                <FieldRow
                  key={field.id}
                  field={field}
                  fieldTypes={fieldTypes}
                  onChange={(patch) => onFieldChange(field.id, patch)}
                  onRemove={() => onFieldRemove(field.id)}
                />
              ))}
            </ul>
          )}
          <Button type="button" variant="outline" size="sm" onClick={onAddField}>
            <Plus aria-hidden="true" />
            Add a field
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

export default ListEditor;
